import React, { useState } from 'react';
import { useZxing } from 'react-zxing';
import { Camera, CameraOff } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { fetchProductInfo } from '../lib/openFoodFacts';
import { useStore } from '../store';
import { useAuth } from '../hooks/useAuth';
import type { Product } from '../types';

export const Scanner: React.FC = () => {
  const [isScanning, setIsScanning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasScanned, setHasScanned] = useState(false);
  const [lastScannedBarcode, setLastScannedBarcode] = useState<string | null>(null);
  const addToHistory = useStore((state) => state.addToHistory);
  const { user } = useAuth();

  const handleResult = async (barcode: string) => {
    // Prevent duplicate scans of the same barcode in quick succession
    if (isLoading || barcode === lastScannedBarcode) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setLastScannedBarcode(barcode);
    
    try {
      // First try Open Food Facts
      const productInfo = await fetchProductInfo(barcode);
      
      if (!productInfo) {
        // If not found in OpenFoodFacts, check our database
        const { data: products, error: dbError } = await supabase
          .from('products')
          .select('*')
          .eq('barcode', barcode)
          .single();

        if (dbError) throw dbError;

        if (products) {
          const product: Product = {
            id: products.id,
            barcode: products.barcode,
            name: products.name,
            brand: products.brand,
            status: products.status,
            statusReason: 'Status from database',
            certifiedBy: products.certified_by,
            ingredients: products.ingredients,
            imageUrl: products.image_url,
            createdAt: products.created_at,
            updatedAt: products.updated_at,
          };

          // Save to scan history if user is logged in
          if (user) {
            const { error: historyError } = await supabase
              .from('scan_history')
              .insert({
                user_id: user.id,
                product_id: product.id,
                scanned_at: new Date().toISOString()
              });

            if (historyError) {
              console.error('Error saving to scan history:', historyError);
            }
          }

          addToHistory(product);
          setIsScanning(false);
          setHasScanned(true);
          return;
        }

        setError('Product not found');
        return;
      }

      // If found in OpenFoodFacts, upsert to our database
      const { data: newProduct, error: upsertError } = await supabase
        .from('products')
        .upsert({
          barcode,
          name: productInfo.name,
          brand: productInfo.brand,
          status: productInfo.status,
          ingredients: productInfo.ingredients,
          image_url: productInfo.imageUrl,
        }, {
          onConflict: 'barcode'
        })
        .select()
        .single();

      if (upsertError) {
        console.error('Error upserting product:', upsertError);
        throw upsertError;
      }

      if (!newProduct) {
        throw new Error('Failed to save product to database');
      }

      // Create the product object
      const product: Product = {
        id: newProduct.id,
        barcode,
        name: productInfo.name,
        brand: productInfo.brand,
        status: productInfo.status,
        statusReason: productInfo.statusReason,
        certifiedBy: productInfo.certifiedBy,
        ingredients: productInfo.ingredients,
        imageUrl: productInfo.imageUrl,
        createdAt: newProduct.created_at,
        updatedAt: newProduct.updated_at,
      };

      // Save to scan history if user is logged in
      if (user) {
        const { error: historyError } = await supabase
          .from('scan_history')
          .insert({
            user_id: user.id,
            product_id: newProduct.id,
            scanned_at: new Date().toISOString()
          });

        if (historyError) {
          console.error('Error saving to scan history:', historyError);
          throw historyError;
        }
      }
      
      addToHistory(product);
      setIsScanning(false);
      setHasScanned(true);
    } catch (err) {
      console.error('Error processing barcode:', err);
      setError('Failed to process product. Please try again.');
    } finally {
      setIsLoading(false);
      // Clear the last scanned barcode after a delay
      setTimeout(() => {
        setLastScannedBarcode(null);
      }, 2000); // Prevent rescanning for 2 seconds
    }
  };

  const { ref } = useZxing({
    onDecodeResult: (result) => handleResult(result.getText()),
    onError: (error) => {
      console.error('Scanner error:', error);
      // Only show camera access errors to the user
      if (error.name === 'NotAllowedError' || error.name === 'NotFoundError') {
        setError('Failed to access camera. Please make sure you have granted camera permissions.');
      }
    },
    paused: !isScanning,
    constraints: {
      video: {
        facingMode: 'environment',
        width: { ideal: 1280 },
        height: { ideal: 720 }
      }
    }
  });

  return (
    <div className="w-full max-w-md mx-auto">
      <button
        onClick={() => {
          setIsScanning(!isScanning);
          setError(null);
        }}
        disabled={isLoading}
        className="w-full mb-4 flex items-center justify-center gap-2 bg-emerald-600 text-white py-3 px-4 rounded-lg hover:bg-emerald-700 active:bg-emerald-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation min-h-[44px]"
      >
        {isScanning ? (
          <>
            <CameraOff className="w-5 h-5" />
            <span className="text-base sm:text-lg">Stop Scanning</span>
          </>
        ) : (
          <>
            <Camera className="w-5 h-5" />
            <span className="text-base sm:text-lg">
              {hasScanned ? 'Scan another barcode' : 'Scan Barcode'}
            </span>
          </>
        )}
      </button>

      {error && (
        <div className="mb-4 p-3 sm:p-4 bg-red-100 text-red-700 rounded-lg text-sm sm:text-base">
          {error}
        </div>
      )}

      {isScanning && (
        <div className="relative aspect-[4/3] w-full rounded-lg overflow-hidden">
          <video ref={ref} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-[10%] border-2 border-white/50 rounded-lg"></div>
          </div>
          {isLoading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <div className="text-white text-sm sm:text-base">Searching product...</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};